/** Date helpers — en-US by default; pass the visitor's locale for the translated UI. Formatters
 *  are kept per locale and options (src/lib/intl-cache.ts): same text, built once. */
import { localeDate, numberFormat } from './intl-cache';

const parse = (isoDate: string) => new Date(isoDate + 'T12:00:00');

/** "Sat, Jul 19, 2026" */
export const fmtDate = (isoDate: string, locale = 'en-US') =>
  localeDate(parse(isoDate), locale, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

/** "Jul 19" (adds ’27 etc. when not the current year) */
export const fmtDateShort = (isoDate: string, locale = 'en-US') => {
  const d = parse(isoDate);
  const base = localeDate(d, locale, { month: 'short', day: 'numeric' });
  return d.getFullYear() === new Date().getFullYear() ? base : `${base} ’${String(d.getFullYear()).slice(2)}`;
};

/** Whole calendar days from today (0 = today, 1 = tomorrow). */
export const daysFromToday = (isoDate: string) => {
  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((parse(isoDate).setHours(0, 0, 0, 0) - todayMidnight.getTime()) / 864e5);
};

export const kmToMiles = (km: number) => km * 0.621371;

/** Countries whose road distances are in miles. */
const MILE_COUNTRIES = new Set(['US', 'GB', 'LR', 'MM']);

/** Show distances in miles? The visitor's country decides when we know it (a UK visitor reads
 *  miles in any language, a Canadian km); without it, English means miles and the others km. */
export const usesMiles = (country: string | null | undefined, lang: string) =>
  country ? MILE_COUNTRIES.has(country) : lang === 'en';

/** A radius given in miles, as a round km figure for the label (25 → 40, 100 → 160, 250 → 400). */
export const milesAsKm = (miles: number) => Math.max(5, Math.round((miles * 1.609344) / (miles >= 50 ? 10 : 5)) * (miles >= 50 ? 10 : 5));

/**
 * A moment in time with its zone named: "Oct 3, 2:35 AM UTC", "3. Okt., 04:35 MESZ".
 * `timeZone` is an IANA name (a venue's own zone, or useTimeZone()); an unknown
 * one falls back to UTC rather than throwing. `full` adds the weekday and year.
 */
export const fmtMoment = (iso: string, locale = 'en-US', timeZone = 'UTC', full = false) => {
  const opts: Intl.DateTimeFormatOptions = {
    ...(full ? { weekday: 'short', year: 'numeric' } : {}),
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  };
  const d = new Date(iso);
  try {
    return localeDate(d, locale, { ...opts, timeZone });
  } catch {
    return localeDate(d, locale, { ...opts, timeZone: 'UTC' });
  }
};

/** "Sun, Sep 20" — the weekday matters to a ticket buyer; the year only when it isn't this
 *  one ("Thu, Mar 11, 2027"), so an announcement for next spring never reads as this one. */
export const fmtDateDow = (isoDate: string, locale = 'en-US') => {
  const d = parse(isoDate);
  return localeDate(d, locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' as const } : {}),
  });
};

/** "19:00" → "7:00 PM" (en-US) or "19:00" (locales that read the clock that way). Empty when the catalog has no showtime. */
export const fmtTime = (hhmm?: string, locale = 'en-US') => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h)) return '';
  if (locale !== 'en-US') return localeDate(new Date(2000, 0, 1, h, m || 0), locale, { hour: 'numeric', minute: '2-digit' });
  return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

/** "$58" / "$70.15" — cents only when there are cents. One place, one currency,
 *  so the day a second storefront arrives this is the only seam. */
export const fmtMoney = (n: number, currency = 'USD') =>
  numberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(n);

export type PriceMove = { dir: 'up' | 'down' | 'flat'; label: string; key: string; amount?: string };

/** Price movement from our own daily tape, as a buyer reads it: today's change
 *  first ("↓ $42 today"), else this week's ("↑ $8 this week"), else flat.
 *  Every figure is an observed floor-to-floor difference, never an estimate —
 *  so an event we have no recordings for (most partner pages: no day change, no
 *  trend) says nothing at all, not "flat today". */
export const priceMove = (e: { dayChange?: number; trend?: number[] }, money: (n: number) => string = fmtMoney): PriceMove => {
  if (typeof e.dayChange !== 'number' && (e.trend?.length ?? 0) < 2) return { dir: 'flat', label: '', key: '' };
  const dc = e.dayChange ?? 0;
  if (dc) return { dir: dc > 0 ? 'up' : 'down', label: `${dc > 0 ? '↑' : '↓'} ${money(Math.abs(dc))} today`, key: dc > 0 ? '↑ {amount} today' : '↓ {amount} today', amount: money(Math.abs(dc)) };
  const t = e.trend ?? [];
  if (t.length >= 2) {
    const week = t.slice(-7);
    const d = +(week[week.length - 1] - week[0]).toFixed(2);
    if (d) return { dir: d > 0 ? 'up' : 'down', label: `${d > 0 ? '↑' : '↓'} ${money(Math.abs(d))} this week`, key: d > 0 ? '↑ {amount} this week' : '↓ {amount} this week', amount: money(Math.abs(d)) };
  }
  return { dir: 'flat', label: 'flat today', key: 'flat today' };
};
